/**
 * Система логирования для миграций
 */

import { EventEmitter } from 'events';
import { MigrationExecutionInfo } from './MigrationRunner';
import { MigrationTask } from './MigrationScheduler';
import { IMigration, MigrationResult } from '../types/migration-interfaces';

/**
 * Уровень логирования
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Запись лога
 */
export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  source: string;
  message: string;
  context?: Record<string, any>;
  migrationId?: string;
  taskId?: string;
}

/**
 * Конфигурация логгера
 */
export interface MigrationLoggerConfig {
  // Минимальный уровень логирования
  minLevel?: LogLevel;
  
  // Включить логирование в консоль
  console?: boolean;
  
  // Максимальное количество записей в памяти
  maxEntries?: number;
  
  // Форматирование сообщений
  formatter?: (entry: LogEntry) => string;
  
  // Фильтр записей
  filter?: (entry: LogEntry) => boolean;
  
  // Настройки для разных источников
  sources?: {
    [source: string]: {
      enabled?: boolean;
      level?: LogLevel;
    };
  };
}

/**
 * Интерфейс для вывода логов
 */
export interface LogOutput {
  write(entry: LogEntry): Promise<void>;
  flush?(): Promise<void>;
  close?(): Promise<void>;
}

/**
 * Консольный вывод
 */
export class ConsoleLogOutput implements LogOutput {
  private colors: Record<LogLevel, string> = {
    debug: '\x1b[36m', // Cyan
    info: '\x1b[32m',  // Green
    warn: '\x1b[33m',  // Yellow
    error: '\x1b[31m'  // Red
  };

  private reset = '\x1b[0m';

  async write(entry: LogEntry): Promise<void> {
    const color = this.colors[entry.level];
    const timestamp = entry.timestamp.toISOString();
    const level = entry.level.toUpperCase().padEnd(5);
    const source = entry.source.padEnd(15);
    
    const message = `${color}[${timestamp}] ${level} ${source}${this.reset} ${entry.message}`;
    
    if (entry.level === 'error') {
      console.error(message);
    } else if (entry.level === 'warn') {
      console.warn(message);
    } else {
      console.log(message);
    }

    if (entry.context) {
      console.log('  Context:', JSON.stringify(entry.context, null, 2));
    }
  }
}

/**
 * Вывод в память
 */
export class MemoryLogOutput implements LogOutput {
  private entries: LogEntry[] = [];
  private maxEntries: number;

  constructor(maxEntries: number = 1000) {
    this.maxEntries = maxEntries;
  }

  async write(entry: LogEntry): Promise<void> {
    this.entries.push(entry);
    
    // Удаляем старые записи если превышен лимит
    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }
  }

  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  clear(): void {
    this.entries = [];
  }
}

/**
 * Логгер миграций
 */
export class MigrationLogger extends EventEmitter {
  private config: MigrationLoggerConfig;
  private outputs: LogOutput[] = [];
  private memoryOutput: MemoryLogOutput;
  private levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  };

  constructor(config: MigrationLoggerConfig = {}) {
    super();
    this.config = {
      minLevel: 'info',
      console: true,
      maxEntries: 1000,
      formatter: this.defaultFormatter,
      ...config
    };

    // Добавляем память для хранения логов
    this.memoryOutput = new MemoryLogOutput(this.config.maxEntries);
    this.outputs.push(this.memoryOutput);

    // Добавляем консольный вывод если включен
    if (this.config.console) {
      this.outputs.push(new ConsoleLogOutput());
    }
  }

  /**
   * Форматирование по умолчанию
   */
  private defaultFormatter(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString();
    const level = entry.level.toUpperCase();
    const source = entry.source;
    
    let message = `[${timestamp}] ${level} ${source}: ${entry.message}`;
    
    if (entry.migrationId) {
      message += ` (migration: ${entry.migrationId})`;
    }
    
    if (entry.taskId) {
      message += ` (task: ${entry.taskId})`;
    }
    
    return message;
  }

  /**
   * Добавление вывода
   */
  addOutput(output: LogOutput): void {
    this.outputs.push(output);
  }

  /**
   * Удаление вывода
   */
  removeOutput(output: LogOutput): void {
    const index = this.outputs.indexOf(output);
    if (index >= 0) {
      this.outputs.splice(index, 1);
    }
  }

  /**
   * Логирование сообщения
   */
  async log(level: LogLevel, source: string, message: string, context?: Record<string, any>): Promise<void> {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      source,
      message,
      context
    };

    // Проверяем уровень логирования
    if (this.levelPriority[level] < this.levelPriority[this.config.minLevel!]) {
      return;
    }

    // Проверяем настройки для источника
    const sourceConfig = this.config.sources?.[source];
    if (sourceConfig) {
      if (sourceConfig.enabled === false) {
        return;
      }
      if (sourceConfig.level && this.levelPriority[level] < this.levelPriority[sourceConfig.level]) {
        return;
      }
    }

    // Применяем фильтр
    if (this.config.filter && !this.config.filter(entry)) {
      return;
    }

    // Отправляем во все выводы
    const promises = this.outputs.map(output => output.write(entry));
    await Promise.all(promises);

    // Эмитируем событие
    this.emit('log', entry);
  }

  /**
   * Логирование отладки
   */
  async debug(source: string, message: string, context?: Record<string, any>): Promise<void> {
    return this.log('debug', source, message, context);
  }

  /**
   * Логирование информации
   */
  async info(source: string, message: string, context?: Record<string, any>): Promise<void> {
    return this.log('info', source, message, context);
  }

  /**
   * Логирование предупреждения
   */
  async warn(source: string, message: string, context?: Record<string, any>): Promise<void> {
    return this.log('warn', source, message, context);
  }

  /**
   * Логирование ошибки
   */
  async error(source: string, message: string, context?: Record<string, any>): Promise<void> {
    return this.log('error', source, message, context);
  }

  /**
   * Логирование начала выполнения миграции
   */
  async logMigrationStart(migration: IMigration): Promise<void> {
    await this.info('migration', `Starting migration: ${migration.id}`, {
      migrationId: migration.id,
      version: migration.version,
      description: migration.description,
      type: migration.type
    });
  }

  /**
   * Логирование успешного завершения миграции
   */
  async logMigrationSuccess(migration: IMigration, result: MigrationResult): Promise<void> {
    await this.info('migration', `Migration completed successfully: ${migration.id}`, {
      migrationId: migration.id,
      processedCount: result.processedCount,
      executionTime: result.executionTime,
      message: result.message
    });
  }

  /**
   * Логирование ошибки миграции
   */
  async logMigrationError(migration: IMigration, error: Error): Promise<void> {
    await this.error('migration', `Migration failed: ${migration.id}`, {
      migrationId: migration.id,
      error: error.message,
      stack: error.stack
    });
  }

  /**
   * Логирование начала выполнения задачи
   */
  async logTaskStart(task: MigrationTask): Promise<void> {
    await this.info('task', `Starting task: ${task.id}`, {
      taskId: task.id,
      trigger: task.trigger,
      scheduledAt: task.scheduledAt
    });
  }

  /**
   * Логирование завершения задачи
   */
  async logTaskComplete(task: MigrationTask): Promise<void> {
    const level = task.status === 'completed' ? 'info' : 'error';
    await this.log(level, 'task', `Task ${task.status}: ${task.id}`, {
      taskId: task.id,
      status: task.status,
      result: task.result,
      error: task.error
    });
  }

  /**
   * Логирование выполнения плана миграций
   */
  async logExecutionInfo(info: MigrationExecutionInfo): Promise<void> {
    const level = info.status === 'completed' ? 'info' : 'error';
    await this.log(level, 'execution', `Migration execution ${info.status}`, {
      totalMigrations: info.totalMigrations,
      completedMigrations: info.completedMigrations,
      failedMigrations: info.failedMigrations,
      totalExecutionTime: info.totalExecutionTime,
      errors: info.errors
    });
  }

  /**
   * Получение записей из памяти
   */
  getEntries(filter?: {
    level?: LogLevel;
    source?: string;
    migrationId?: string;
    taskId?: string;
    since?: Date;
    until?: Date;
  }): LogEntry[] {
    let entries = this.memoryOutput.getEntries();

    if (filter) {
      entries = entries.filter(entry => {
        if (filter.level && entry.level !== filter.level) {
          return false;
        }
        if (filter.source && entry.source !== filter.source) {
          return false;
        }
        if (filter.migrationId && entry.migrationId !== filter.migrationId) {
          return false;
        }
        if (filter.taskId && entry.taskId !== filter.taskId) {
          return false;
        }
        if (filter.since && entry.timestamp < filter.since) {
          return false;
        }
        if (filter.until && entry.timestamp > filter.until) {
          return false;
        }
        return true;
      });
    }

    return entries;
  }

  /**
   * Очистка логов
   */
  clear(): void {
    this.memoryOutput.clear();
  }

  /**
   * Получение статистики
   */
  getStats(): {
    totalEntries: number;
    byLevel: Record<LogLevel, number>;
    bySource: Record<string, number>;
  } {
    const entries = this.memoryOutput.getEntries();
    
    const byLevel: Record<LogLevel, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0
    };
    
    const bySource: Record<string, number> = {};
    
    for (const entry of entries) {
      byLevel[entry.level]++;
      bySource[entry.source] = (bySource[entry.source] || 0) + 1;
    }
    
    return {
      totalEntries: entries.length,
      byLevel,
      bySource
    };
  }

  /**
   * Закрытие логгера
   */
  async close(): Promise<void> {
    const promises = this.outputs
      .filter(output => output.close)
      .map(output => output.close!());
    
    await Promise.all(promises);
  }
} 